/**
 * ============================================================================
 * ADMIN ESCROWS PAGE
 * ============================================================================
 * Same structure and UX as Admin Finance/Transactions: hero, stats bento,
 * filters, escrow list, pagination.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAdminStore } from '../../store';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Spinner from '../../components/common/Spinner';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

/** Normalize backend escrow (snake_case + nested transaction) for display */
function normalizeEscrow(e) {
  if (!e) return null;
  const tx = e.transaction || {};
  return {
    ...e,
    transactionId: e.transaction_id ?? e.transactionId ?? tx?.id,
    createdAt: e.created_at ?? e.createdAt,
    amount: e.amount ?? tx?.amount,
    currency: e.currency ?? tx?.currency ?? 'XAF',
  };
}

const AdminEscrows = () => {
  const navigate = useNavigate();
  const { listAllEscrows, loading } = useAdmin();
  const { filters, setFilters, pagination, setPagination } = useAdminStore();
  const { showError } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [escrowsList, setEscrowsList] = useState([]);
  const [escrowsPagination, setEscrowsPagination] = useState(null);
  const [searchQuery, setSearchQuery] = useState(filters?.search || '');
  const [statusFilter, setStatusFilter] = useState(filters?.status ?? '');
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const response = await listAllEscrows({
          status: statusFilter || undefined,
          page: pagination?.page || 1,
          limit: pagination?.pageSize || 20,
        });
        const list = Array.isArray(response?.data) ? response.data : [];
        setEscrowsList(list);
        const pag = response?.pagination ?? null;
        setEscrowsPagination(pag);
        if (pag) {
          setPagination({
            ...pagination,
            page: pag.page,
            total: pag.total,
            totalPages: pag.pages ?? Math.ceil((pag.total ?? 0) / (pag.limit ?? 20)),
          });
        }
      } catch (error) {
        showError(
          error.message ||
            (language === 'fr' ? 'Échec du chargement des escrows' : 'Failed to load escrows')
        );
        console.error('Load escrows error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    listAllEscrows,
    statusFilter,
    showError,
    language,
    pagination?.page,
    pagination?.pageSize,
  ]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchQuery });
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setStatusFilter(value);
    setPagination({ ...pagination, page: 1 });
  };

  const clearFilters = () => {
    setFilters({ search: null, status: null });
    setSearchQuery('');
    setStatusFilter('');
    setPagination({ ...pagination, page: 1 });
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      funded: '🔒',
      frozen: '❄️',
      released: '✅',
      refunded: '↩️',
      partial: '📊',
      cancelled: '❌',
    };
    return icons[status] || '📦';
  };

  const list = Array.isArray(escrowsList) ? escrowsList : [];
  const stats = {
    total: escrowsPagination?.total ?? list.length,
    pending: list.filter((e) => e.status === 'pending').length,
    funded: list.filter((e) => e.status === 'funded').length,
    totalAmount: list.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0),
  };

  const statusOptions = [
    { value: '', label: language === 'fr' ? 'Tous les statuts' : 'All Statuses' },
    { value: 'pending', label: language === 'fr' ? 'En attente' : 'Pending' },
    { value: 'funded', label: language === 'fr' ? 'Financé' : 'Funded' },
    { value: 'frozen', label: language === 'fr' ? 'Gelé' : 'Frozen' },
    { value: 'released', label: language === 'fr' ? 'Libéré' : 'Released' },
    { value: 'refunded', label: language === 'fr' ? 'Remboursé' : 'Refunded' },
    { value: 'cancelled', label: language === 'fr' ? 'Annulé' : 'Cancelled' },
  ];

  const totalFromPagination = escrowsPagination?.total ?? list.length;
  const showPagination = totalFromPagination > (pagination?.pageSize ?? 20);

  if (isLoading || loading) {
    return (
      <AdminLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement des escrows...' : 'Loading escrows...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              🔒 {language === 'fr' ? 'Gestion des Escrows' : 'Escrow Management'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez et surveillez tous les escrows du système'
                : 'Manage and monitor all escrows in the system'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Stats - Bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {[
                {
                  icon: '📦',
                  label: language === 'fr' ? 'Total' : 'Total',
                  value: stats.total,
                },
                {
                  icon: '⏳',
                  label: language === 'fr' ? 'En attente' : 'Pending',
                  value: stats.pending,
                },
                {
                  icon: '🔒',
                  label: language === 'fr' ? 'Financés' : 'Funded',
                  value: stats.funded,
                },
                {
                  icon: '💰',
                  label: language === 'fr' ? 'Montant (page)' : 'Amount (page)',
                  value: formatCurrency(stats.totalAmount),
                },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters & List */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="transactions-wt-filters dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  {language === 'fr' ? 'Filtres' : 'Filters'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <form onSubmit={handleSearch} className="transactions-wt-form">
                  <div className="transactions-wt-form-row">
                    <div className="transactions-wt-form-search">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={
                          language === 'fr'
                            ? 'ID transaction, utilisateur...'
                            : 'Transaction ID, user...'
                        }
                        className="transactions-wt-input"
                      />
                    </div>
                    <div className="transactions-wt-form-select">
                      <Select
                        value={statusFilter || ''}
                        onChange={(e) =>
                          handleFilterChange('status', e.target.value || null)
                        }
                        options={statusOptions}
                      />
                    </div>
                    <button type="submit" className="dashboard-wt-btn-primary">
                      {language === 'fr' ? 'Rechercher' : 'Search'}
                    </button>
                    {(filters?.search || filters?.status) && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary"
                        onClick={clearFilters}
                      >
                        {language === 'fr' ? 'Effacer' : 'Clear'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {list.length === 0 ? (
              <div className="dashboard-wt-card dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">🔒</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr' ? 'Aucun escrow' : 'No escrows found'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Aucun escrow ne correspond à vos filtres'
                    : 'No escrows match your filters'}
                </p>
                <button
                  type="button"
                  className="dashboard-wt-btn-secondary dashboard-wt-btn-large"
                  onClick={clearFilters}
                >
                  {language === 'fr' ? 'Effacer les filtres' : 'Clear Filters'}
                </button>
              </div>
            ) : (
              <>
                <div className="dashboard-wt-transactions transactions-wt-list">
                  {list.map((escrow, index) => {
                    const e = normalizeEscrow(escrow);
                    return (
                      <div
                        key={escrow.id}
                        className="dashboard-wt-transaction-wrapper dashboard-wt-card"
                        style={{ animationDelay: `${index * 0.05}s` }}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/admin/escrows/${escrow.id}`)}
                        onKeyDown={(ev) =>
                          ev.key === 'Enter' && navigate(`/admin/escrows/${escrow.id}`)
                        }
                      >
                        <div className="dashboard-wt-card-content">
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/80 to-cyan-600/80 flex items-center justify-center text-xl shadow-lg flex-shrink-0">
                              {getStatusIcon(escrow.status)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="font-bold text-[#e5e5e5]">
                                  {language === 'fr' ? 'Escrow' : 'Escrow'} #{escrow.id?.slice(0, 8) ?? 'N/A'}
                                </h3>
                                <span
                                  className={`text-xs px-2 py-0.5 rounded ${
                                    escrow.status === 'funded'
                                      ? 'bg-green-500/20 text-green-400'
                                      : escrow.status === 'pending'
                                      ? 'bg-amber-500/20 text-amber-400'
                                      : escrow.status === 'frozen'
                                      ? 'bg-red-500/20 text-red-400'
                                      : 'bg-[#2a2a2a] text-[#a3a3a3]'
                                  }`}
                                >
                                  {(escrow.status || '').toUpperCase()}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#737373]">
                                <span>
                                  {language === 'fr' ? 'Montant' : 'Amount'}:{' '}
                                  {formatCurrency(e.amount ?? 0, e.currency)}
                                </span>
                                {e.transactionId && (
                                  <>
                                    <span>·</span>
                                    <span>
                                      {language === 'fr' ? 'Transaction' : 'Transaction'}: #{e.transactionId?.slice(0, 8)}
                                    </span>
                                  </>
                                )}
                                <span>·</span>
                                <span>{e.createdAt && formatDate(e.createdAt)}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="dashboard-wt-btn-primary flex-shrink-0"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/admin/escrows/${escrow.id}`);
                              }}
                            >
                              {language === 'fr' ? 'Voir' : 'View'} →
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {showPagination && (
                  <div className="transactions-wt-pagination dashboard-wt-card">
                    <div className="transactions-wt-pagination-inner">
                      <span className="transactions-wt-pagination-text">
                        {language === 'fr' ? 'Affichage' : 'Showing'}{' '}
                        {(pagination.page - 1) * (pagination.pageSize || 20) + 1}{' '}
                        {language === 'fr' ? 'à' : 'to'}{' '}
                        {Math.min(
                          pagination.page * (pagination.pageSize || 20),
                          totalFromPagination
                        )}{' '}
                        {language === 'fr' ? 'sur' : 'of'}{' '}
                        {totalFromPagination}
                      </span>
                      <div className="transactions-wt-pagination-btns">
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({ ...pagination, page: pagination.page - 1 })
                          }
                          disabled={pagination.page <= 1}
                        >
                          {language === 'fr' ? 'Précédent' : 'Previous'}
                        </button>
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({ ...pagination, page: pagination.page + 1 })
                          }
                          disabled={
                            pagination.page * (pagination.pageSize || 20) >=
                            totalFromPagination
                          }
                        >
                          {language === 'fr' ? 'Suivant' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminEscrows;
